import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Loader2 } from "lucide-react";
import type { ChurnRiskAlertSettings } from "@shared/schema";

type ChurnRiskAlertSettingsResponse = {
  settings: ChurnRiskAlertSettings | null;
};

const DEFAULTS = {
  enabled: true,
  sendHourUtc: 8,
  recipients: [] as string[],
  alertCoolingOff: false,
  alertAtRisk: true,
  alertDormant: true,
};

export function ChurnRiskAlertsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [enabled, setEnabled] = useState(DEFAULTS.enabled);
  const [sendHourUtc, setSendHourUtc] = useState(DEFAULTS.sendHourUtc);
  const [recipientsText, setRecipientsText] = useState("");
  const [alertCoolingOff, setAlertCoolingOff] = useState(DEFAULTS.alertCoolingOff);
  const [alertAtRisk, setAlertAtRisk] = useState(DEFAULTS.alertAtRisk);
  const [alertDormant, setAlertDormant] = useState(DEFAULTS.alertDormant);
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery<ChurnRiskAlertSettingsResponse>({
    queryKey: ["/api/admin/settings/churn-risk-alerts"],
    queryFn: async () => {
      const res = await fetch("/api/admin/settings/churn-risk-alerts", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load churn-risk alert settings");
      return res.json();
    },
  });

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      const s = settingsQuery.data.settings;
      setEnabled(s?.enabled ?? DEFAULTS.enabled);
      setSendHourUtc(s?.sendHourUtc ?? DEFAULTS.sendHourUtc);
      setRecipientsText((s?.recipients ?? DEFAULTS.recipients).join("\n"));
      setAlertCoolingOff(s?.alertCoolingOff ?? DEFAULTS.alertCoolingOff);
      setAlertAtRisk(s?.alertAtRisk ?? DEFAULTS.alertAtRisk);
      setAlertDormant(s?.alertDormant ?? DEFAULTS.alertDormant);
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const parseRecipients = (text: string): string[] =>
    Array.from(
      new Set(
        text
          .split(/[\n,]/)
          .map((e) => e.trim().toLowerCase())
          .filter((e) => e.length > 0),
      ),
    );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const recipients = parseRecipients(recipientsText);
      const invalid = recipients.find((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      if (invalid) throw new Error(`"${invalid}" is not a valid email address`);
      return apiRequest("PUT", "/api/admin/settings/churn-risk-alerts", {
        enabled,
        sendHourUtc,
        recipients,
        alertCoolingOff,
        alertAtRisk,
        alertDormant,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings/churn-risk-alerts"] });
      toast({
        title: "Churn-risk alert settings saved",
        description: "The daily digest will use these settings on its next run.",
      });
    },
    onError: (err: any) =>
      toast({
        title: "Failed to save",
        description: parseErrorMessage(err, "Couldn't save churn-risk alert settings. Please try again."),
        variant: "destructive",
      }),
  });

  const updatedBy = settingsQuery.data?.settings?.updatedBy ?? null;
  const updatedAt = settingsQuery.data?.settings?.updatedAt ?? null;
  const noBucketSelected = !alertCoolingOff && !alertAtRisk && !alertDormant;
  const recipientCount = parseRecipients(recipientsText).length;

  return (
    <div className="max-w-2xl space-y-4">
      <Card className="p-5 space-y-5">
        <div>
          <h3 className="font-semibold text-sm mb-1" data-testid="text-churn-alerts-title">
            Churn-Risk Alert Digest
          </h3>
          <p className="text-xs text-muted-foreground">
            The daily digest emails (and Slacks) the admin team the users who just slid into a worse
            engagement bucket so you can reach out before they churn. Tune who receives it, what UTC
            hour it sends, and which transitions trigger an alert. Changes take effect on the next run
            — no redeploy required.
          </p>
        </div>

        {settingsQuery.isLoading ? (
          <div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : settingsQuery.isError ? (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-3 text-xs space-y-2"
            data-testid="status-churn-alerts-error"
          >
            <p className="text-destructive flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Couldn't load the current settings. Saving is disabled to avoid overwriting them.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => settingsQuery.refetch()}
              data-testid="button-retry-churn-alerts"
            >
              Retry
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-md border px-3 py-3">
              <div className="space-y-0.5">
                <Label className="text-sm">Digest enabled</Label>
                <p className="text-[11px] text-muted-foreground">
                  When off, the daily cron skips the digest entirely.
                </p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="switch-churn-alerts-enabled"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Send hour (UTC)</Label>
              <Select
                value={String(sendHourUtc)}
                onValueChange={(v) => setSendHourUtc(Number(v))}
                disabled={!enabled}
              >
                <SelectTrigger className="w-full" data-testid="select-churn-alerts-hour">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, h) => (
                    <SelectItem key={h} value={String(h)} data-testid={`option-churn-hour-${h}`}>
                      {String(h).padStart(2, "0")}:00 UTC
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                The digest fires once per day at this hour.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Recipients</Label>
              <Textarea
                value={recipientsText}
                onChange={(e) => setRecipientsText(e.target.value)}
                placeholder="One email per line. Leave blank to send to all admins."
                rows={4}
                disabled={!enabled}
                data-testid="textarea-churn-alerts-recipients"
              />
              <p className="text-[11px] text-muted-foreground">
                {recipientCount === 0
                  ? "No custom recipients — the digest goes to all admin emails."
                  : `${recipientCount} custom recipient${recipientCount === 1 ? "" : "s"} (overrides the admin list).`}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Alert on these transitions</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Newly Dormant</Label>
                    <p className="text-[11px] text-muted-foreground">Crossed into the Dormant bucket.</p>
                  </div>
                  <Switch
                    checked={alertDormant}
                    onCheckedChange={setAlertDormant}
                    disabled={!enabled}
                    data-testid="switch-churn-alert-dormant"
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Newly At Risk</Label>
                    <p className="text-[11px] text-muted-foreground">Crossed into the At risk bucket.</p>
                  </div>
                  <Switch
                    checked={alertAtRisk}
                    onCheckedChange={setAlertAtRisk}
                    disabled={!enabled}
                    data-testid="switch-churn-alert-at-risk"
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2.5">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Newly Cooling Off</Label>
                    <p className="text-[11px] text-muted-foreground">
                      Earliest warning — crossed into the Cooling off bucket.
                    </p>
                  </div>
                  <Switch
                    checked={alertCoolingOff}
                    onCheckedChange={setAlertCoolingOff}
                    disabled={!enabled}
                    data-testid="switch-churn-alert-cooling-off"
                  />
                </div>
              </div>
              {enabled && noBucketSelected && (
                <p className="text-[11px] text-destructive flex items-center gap-1" data-testid="text-churn-no-bucket-warning">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  No transitions selected — the digest will never send an alert.
                </p>
              )}
            </div>

            {updatedBy && (
              <div className="rounded-md border bg-muted/40 px-3 py-2.5 text-xs" data-testid="status-churn-alerts">
                <p className="text-muted-foreground">
                  Last changed by {updatedBy}
                  {updatedAt ? ` on ${new Date(updatedAt).toLocaleString()}` : ""}
                </p>
              </div>
            )}

            <Button
              className="w-full"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              data-testid="button-save-churn-alerts"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
